import React from 'react';
import { Box, Typography, Avatar, Chip } from '@mui/material';
import { CheckCircle as CheckIcon } from '@mui/icons-material';
import { TeamMember } from '../../types';
import { useTranslation } from 'react-i18next';

interface StaffSelectionProps {
  teamMembers: TeamMember[];
  selectedMember: TeamMember | null;
  onSelect: (member: TeamMember) => void;
}


const StaffSelection: React.FC<StaffSelectionProps> = ({ teamMembers, selectedMember, onSelect }) => {
  const { t } = useTranslation();
  const activeMembers = teamMembers.filter(m => m.isActive);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t('booking.steps.selectStaff')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        {t('staffSelection.subtitle')}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: 2,
        }}
      >
        {activeMembers.map((member) => {
          const isSelected = selectedMember?.id === member.id;
          const initials = member.name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);

          return (
            <Box
              key={member.id}
              onClick={() => onSelect(member)}
              sx={{
                position: 'relative',
                borderRadius: 3,
                border: '2px solid',
                borderColor: isSelected ? 'primary.main' : 'divider',
                bgcolor: isSelected ? 'primary.50' : 'background.paper',
                cursor: 'pointer',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                boxShadow: isSelected
                  ? '0 0 0 1px rgba(0,0,0,0.05), 0 4px 16px rgba(0,0,0,0.1)'
                  : '0 1px 4px rgba(0,0,0,0.06)',
                '&:hover': {
                  borderColor: 'primary.light',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {/* Selected checkmark */}
              {isSelected && (
                <CheckIcon
                  sx={{
                    position: 'absolute',
                    top: 10,
                    right: 10,
                    color: 'primary.main',
                    fontSize: 22,
                  }}
                />
              )}

              {/* Top banner with avatar */}
              <Box
                sx={{
                  bgcolor: isSelected ? 'primary.main' : 'grey.100',
                  py: 3,
                  display: 'flex',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s',
                }}
              >
                <Avatar
                  src={member.imageUrl || ''}
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: isSelected ? 'rgba(255,255,255,0.25)' : 'primary.main',
                    color: '#fff',
                    fontSize: '1.6rem',
                    fontWeight: 700,
                    border: '3px solid',
                    borderColor: isSelected ? 'rgba(255,255,255,0.5)' : 'transparent',
                  }}
                >
                  {!member.imageUrl && initials}
                </Avatar>
              </Box>

              {/* Info */}
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  align="center"
                  sx={{ mb: 0.5, color: isSelected ? 'primary.main' : 'text.primary' }}
                >
                  {member.name}
                </Typography>

                {member.role && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ mb: 1.5, fontSize: '0.8rem' }}
                  >
                    {member.role}
                  </Typography>
                )}

                {/* CTA chip */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Chip
                    label={isSelected ? '✓ Sélectionné' : 'Choisir'}
                    size="small"
                    color={isSelected ? 'primary' : 'default'}
                    variant={isSelected ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 600, fontSize: '0.75rem', px: 0.5 }}
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default StaffSelection;
